import { useState, useEffect } from "react";
import { getUserId } from "../../../Services/auth";
import faradayApi from "../../../Services/faradayApi";
import type { SimulationHistoryItem } from "../types";
import { normalizeSimulationHistoryItem } from "./normalizeSimulationHistoryItem";

export function filterSimulationHistory(
  history: SimulationHistoryItem[],
  filter: string
) {
  const normalizedFilter = filter.trim();
  if (!normalizedFilter) return history;

  const cleanFilter = normalizedFilter
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase();
  const isCnpjFilter = /^[A-Za-z0-9.\-/\s]+$/.test(normalizedFilter);
  const upperFilter = normalizedFilter.toUpperCase();

  return history.filter((item) => {
    const cnpj = String(item.cnpj ?? "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase();
    const clientName = String(item.nomeCliente ?? "").toUpperCase();

    return (
      (isCnpjFilter && cleanFilter !== "" && cnpj.includes(cleanFilter)) ||
      clientName.includes(upperFilter)
    );
  });
}

export function useSimulationHistory(
  cnpj: string,
  internalPartnerPermission: boolean
) {
  const [history, setHistory] = useState<SimulationHistoryItem[]>([]);
  const [clientHistory, setClientHistory] = useState<SimulationHistoryItem[]>(
    []
  );
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    const cleanCnpj = cnpj.replace(/[^a-zA-Z0-9]/g, "");
    if (cleanCnpj.length === 14 && internalPartnerPermission) {
      fetchHistory(cleanCnpj);
    } else {
      setClientHistory([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cnpj, internalPartnerPermission]);

  const fetchHistory = async (cleanCnpj?: string) => {
    setLoadingHistory(true);
    try {
      const userId = getUserId();
      const response = cleanCnpj
        ? await faradayApi.get(`simulator-invoice/client/${cleanCnpj}`)
        : await faradayApi.get(`simulator-invoice/user/${userId}`);

      if (response?.data && Array.isArray(response.data)) {
        const parsedHistory = response.data
          .map((item: any) => {
            if (item.simulation_data) {
              try {
                const simulationData =
                  typeof item.simulation_data === "string"
                    ? JSON.parse(item.simulation_data)
                    : item.simulation_data;

                return normalizeSimulationHistoryItem({
                  ...simulationData,
                  simulationType: simulationData.simulationType,
                  partner_name: item.partner_name,
                });
              } catch {
                return null;
              }
            }
            return null;
          })
          .filter(
            (
              item: SimulationHistoryItem | null
            ): item is SimulationHistoryItem => item !== null
          );

        if (cleanCnpj) setClientHistory(parsedHistory);
        setHistory(parsedHistory);
      } else {
        setHistory([]);
        if (cleanCnpj) setClientHistory([]);
      }
    } catch {
      setHistory([]);
      if (cleanCnpj) setClientHistory([]);
    }
    setLoadingHistory(false);
  };

  return {
    history,
    clientHistory,
    loadingHistory,
    fetchHistory,
  };
}
